import { handleErrorResponse } from './api-error';
import type { ApiData, ApiOptions, BaseApiOptions } from './api-options';
import { parseResponseBody } from './api-response';
import { buildUrl } from './api-url';
export type { BaseApiOptions } from './api-options';

/**
 * API client interface with HTTP method helpers.
 * Provides a type-safe wrapper around fetch with automatic JSON handling,
 * query parameters, and error handling.
 */
export interface Api {
  /**
   * Makes an HTTP request to the specified URL.
   *
   * @param url - URL path appended to baseUrl
   * @param options - Request options including body, query params, headers, etc.
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  <T = unknown>(url: string, options?: BaseApiOptions): Promise<T>;

  /**
   * Makes a GET request to the specified URL.
   *
   * @param url - URL path appended to baseUrl
   * @param options - Request options (body not allowed for GET)
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  get: <T = unknown>(url: string, options?: ApiOptions) => Promise<T>;

  /**
   * Makes a POST request to the specified URL with a body.
   *
   * @param url - URL path appended to baseUrl
   * @param body - Request body (will be JSON.stringify'd unless it's a string)
   * @param options - Additional request options
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  post: <T = unknown>(url: string, body: ApiData, options?: ApiOptions) => Promise<T>;

  /**
   * Makes a PUT request to the specified URL with a body.
   *
   * @param url - URL path appended to baseUrl
   * @param body - Request body (will be JSON.stringify'd unless it's a string)
   * @param options - Additional request options
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  put: <T = unknown>(url: string, body: ApiData, options?: ApiOptions) => Promise<T>;

  /**
   * Makes a PATCH request to the specified URL with a body.
   *
   * @param url - URL path appended to baseUrl
   * @param body - Request body (will be JSON.stringify'd unless it's a string)
   * @param options - Additional request options
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  patch: <T = unknown>(url: string, body: ApiData, options?: ApiOptions) => Promise<T>;

  /**
   * Makes a DELETE request to the specified URL.
   *
   * @param url - URL path appended to baseUrl
   * @param options - Request options (body not allowed for DELETE)
   * @returns Parsed JSON response
   * @throws {ApiError} When response status is >= 400
   */
  delete: <T = unknown>(url: string, options?: ApiOptions) => Promise<T>;
}

/**
 * Options for creating an API client.
 */
export type CreateApiOptions = Omit<BaseApiOptions, 'body' | 'query' | 'headers'> & {
  /** Fetch function to use for making requests (e.g., global fetch or a mock for testing). Defaults to global fetch. */
  fetchFn?: typeof fetch;
  /** Base URL prepended to all request URLs. Defaults to ''. */
  baseUrl?: string;
  /**
   * Default headers for every request. Can be a plain object or a function that returns one.
   * A function is called on every request, useful for dynamic values like CSRF tokens.
   */
  headers?: Record<string, string> | (() => Record<string, string> | Promise<Record<string, string>>);
};

/**
 * Creates an API client with the specified fetch function, base URL, and default options.
 *
 * @param options - Configuration for the API client including fetchFn, baseUrl, and default request options
 * @returns API client with HTTP method helpers
 *
 * @example
 * ```ts
 * // Create an API client with a base URL and default headers
 * const api = createApi({
 *   baseUrl: 'https://api.example.com',
 *   headers: {
 *     'Authorization': 'Bearer token123',
 *   },
 *   credentials: 'include',
 * });
 *
 * // Make requests with the client
 * const users = await api.get<User[]>('/users');
 * const newUser = await api.post<User>('/users', { name: 'John' });
 *
 * // Query parameters are automatically appended
 * const filtered = await api.get<User[]>('/users', {
 *   query: { page: 1, limit: 10 }
 * });
 * // Calls: https://api.example.com/users?page=1&limit=10
 *
 * // For a different host, create a separate API client
 * const otherApi = createApi({ baseUrl: 'https://other-api.com' });
 * ```
 */
export function createApi({
  fetchFn = fetch,
  baseUrl = '',
  headers: defaultHeaders,
  handleResponse: defaultHandleResponse = parseResponseBody,
  handleError: defaultHandleError = handleErrorResponse,
  ...defaultOptions
}: CreateApiOptions = {}): Api {
  async function request<T = unknown>({
    url,
    method = 'GET',
    body,
    handleResponse: requestHandleResponse,
    handleError: requestHandleError,
    ...options
  }: { url: string; method?: string; body?: ApiData } & ApiOptions): Promise<T> {
    const { query, headers, ...fetchOptions } = options;

    const fullUrl = buildUrl(`${baseUrl}${url}`, query);
    const isStringBody = typeof body === 'string';

    const requestInit: RequestInit = {
      ...defaultOptions,
      ...fetchOptions,
      method,
      headers: {
        'Content-Type': isStringBody ? 'text/plain' : 'application/json',
        ...(typeof defaultHeaders === 'function' ? await defaultHeaders() : defaultHeaders),
        ...headers,
      },
    };

    if (body !== undefined) {
      requestInit.body = isStringBody ? body : JSON.stringify(body);
    }

    const response = await fetchFn(fullUrl, requestInit);

    if (response.status >= 400) {
      const onError = requestHandleError ?? defaultHandleError;
      return (await onError(response)) as T;
    }

    const onResponse = requestHandleResponse ?? defaultHandleResponse;
    return (await onResponse(response)) as T;
  }

  function api<T = unknown>(url: string, options: BaseApiOptions = {}): Promise<T> {
    return request<T>({ url, ...options });
  }

  api.get = <T = unknown>(url: string, options: ApiOptions = {}): Promise<T> =>
    request<T>({ url, ...options });

  api.post = <T = unknown>(url: string, body: ApiData, options: ApiOptions = {}): Promise<T> =>
    request<T>({ url, method: 'POST', body, ...options });

  api.put = <T = unknown>(url: string, body: ApiData, options: ApiOptions = {}): Promise<T> =>
    request<T>({ url, method: 'PUT', body, ...options });

  api.patch = <T = unknown>(url: string, body: ApiData, options: ApiOptions = {}): Promise<T> =>
    request<T>({ url, method: 'PATCH', body, ...options });

  api.delete = <T = unknown>(url: string, options: ApiOptions = {}): Promise<T> =>
    request<T>({ url, method: 'DELETE', ...options });

  return api;
}
